import { z } from 'zod'
import { productRoleSchema } from '../../schemas/auth.ts'

/**
 * Variables for the four invitation email templates. Each template gets its
 * own EmailType + Listmonk template id; CTO maintains 4 separate Listmonk
 * templates (HTML + copy in Listmonk admin UI).
 */

/**
 * Common fields shared by every invitation variant.
 *
 * Asymmetry: `first_name` is required (.min(1)) because the call site in
 * `supabase/functions/organization-invitations/index.ts` always supplies
 * a non-empty value, falling back to "there" when the invitation row's
 * `admin_first_name` column is null. `last_name` mirrors the underlying
 * column (nullable on the invitation row) and is passed through as
 * `undefined` when missing — Listmonk templates must render gracefully
 * when it is absent (treat as a decorative addition to the salutation,
 * not a load-bearing field).
 *
 * Each string field is `.trim()`-normalized before length validation so a
 * whitespace-only value (e.g. `"   "`) is rejected the same as an empty
 * string and the rendered email never carries leading/trailing spaces.
 */
const baseInvitation = z.object({
  first_name: z.string().trim().min(1),
  last_name: z.string().trim().min(1).optional(),
  confirmation_url: z.string().url(),
})

/** Common fields + organization_name. Used by 3 of 4 invitation variants. */
const invitationWithOrganization = baseInvitation.extend({
  organization_name: z.string().trim().min(1),
})

/**
 * Tenant onboarding — sent to the first admin of a brand-new tenant
 * (organization). Requires organization_name.
 */
export const tenantOnboardingVariablesSchema = invitationWithOrganization
export type TenantOnboardingVariables = z.infer<typeof tenantOnboardingVariablesSchema>

/**
 * Platform invitation — sent to a platform-level admin (super_admin role).
 * No organization context; the admin gets multi-org access after acceptance.
 */
export const platformInvitationVariablesSchema = baseInvitation
export type PlatformInvitationVariables = z.infer<typeof platformInvitationVariablesSchema>

/**
 * Team-member invitation — sent when adding a user to an existing organization.
 * Requires organization_name. Carries optional `product_role` so the Listmonk
 * template can render the assigned role in the email body once the copy is
 * updated; absence renders nothing today (DCP-137 staged rollout).
 */
export const teamInvitationVariablesSchema = invitationWithOrganization.extend({
  product_role: productRoleSchema.optional(),
})
export type TeamInvitationVariables = z.infer<typeof teamInvitationVariablesSchema>

/**
 * Team-member invitation resend — same payload shape as `team-invitation`,
 * but routed to a separate Listmonk template (typically a reminder/nudge copy).
 */
export const teamInvitationResendVariablesSchema = invitationWithOrganization.extend({
  product_role: productRoleSchema.optional(),
})
export type TeamInvitationResendVariables = z.infer<typeof teamInvitationResendVariablesSchema>
